/**
 * Line-clamp preview + full text in a fixed balloon:
 * opens once at entry cursor, stays fixed until pointer leaves trigger + tooltip.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import './CursorTooltip.css'

const TOOLTIP_HIDE_MS = 200
const TOOLTIP_GAP_PX = 8
/** Used before the portal balloon is measured. */
const TOOLTIP_EST_W = 360
const TOOLTIP_EST_H = 168

function clampTooltipToViewport(
  clientX: number,
  clientY: number,
  width: number,
  height: number,
): { x: number; y: number } {
  const m = 8
  let x = clientX + TOOLTIP_GAP_PX
  let y = clientY + TOOLTIP_GAP_PX
  if (x + width > window.innerWidth - m) x = window.innerWidth - width - m
  if (y + height > window.innerHeight - m) y = clientY - height - TOOLTIP_GAP_PX
  if (x < m) x = m
  if (y < m) y = m
  return { x, y }
}

export interface CursorTooltipProps {
  tip: ReactNode
  className: string
  balloonClassName?: string
  children: ReactNode
}

export function CursorTooltip({ tip, className, balloonClassName, children }: CursorTooltipProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const balloonRef = useRef<HTMLDivElement | null>(null)
  const hideTimerRef = useRef<number | null>(null)
  const showTimerRef = useRef<number | null>(null)
  const pendingRef = useRef({ x: 0, y: 0 })

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current != null) {
      window.clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const clearShowTimer = useCallback(() => {
    if (showTimerRef.current != null) {
      window.clearTimeout(showTimerRef.current)
      showTimerRef.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearShowTimer()
    clearHideTimer()
    hideTimerRef.current = window.setTimeout(() => {
      setOpen(false)
      hideTimerRef.current = null
    }, TOOLTIP_HIDE_MS)
  }, [clearHideTimer, clearShowTimer])

  const openOnceAtPointer = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      clearHideTimer()
      clearShowTimer()
      const { clientX, clientY } = e
      pendingRef.current = { x: clientX, y: clientY }
      
      showTimerRef.current = window.setTimeout(() => {
        setPos(clampTooltipToViewport(clientX, clientY, TOOLTIP_EST_W, TOOLTIP_EST_H))
        setOpen(true)
        requestAnimationFrame(() => {
          const el = balloonRef.current
          const w = el?.offsetWidth ?? TOOLTIP_EST_W
          const h = el?.offsetHeight ?? TOOLTIP_EST_H
          setPos(clampTooltipToViewport(clientX, clientY, w, h))
        })
      }, 500) // 500ms delay before showing
    },
    [clearHideTimer, clearShowTimer],
  )

  useLayoutEffect(() => {
    if (!open || !balloonRef.current) return
    const { x, y } = pendingRef.current
    const { offsetWidth: w, offsetHeight: h } = balloonRef.current
    setPos(clampTooltipToViewport(x, y, w, h))
  }, [open, tip])

  useEffect(() => () => {
    clearHideTimer()
    clearShowTimer()
  }, [clearHideTimer, clearShowTimer])

  const onFocusCapture = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      clearHideTimer()
      clearShowTimer()
      const t = e.target
      if (!(t instanceof HTMLElement)) return
      const r = t.getBoundingClientRect()
      const x = r.left
      const y = r.bottom
      pendingRef.current = { x, y }
      
      showTimerRef.current = window.setTimeout(() => {
        setPos(clampTooltipToViewport(x, y, TOOLTIP_EST_W, TOOLTIP_EST_H))
        setOpen(true)
        requestAnimationFrame(() => {
          const el = balloonRef.current
          const w = el?.offsetWidth ?? TOOLTIP_EST_W
          const h = el?.offsetHeight ?? TOOLTIP_EST_H
          setPos(clampTooltipToViewport(x, y, w, h))
        })
      }, 500)
    },
    [clearHideTimer, clearShowTimer],
  )

  const portalRoot = typeof document !== 'undefined' ? document.body : null
  const portal =
    open && portalRoot
      ? createPortal(
          <div
            ref={balloonRef}
            className={['cursor-tooltip__balloon', balloonClassName].filter(Boolean).join(' ')}
            style={{ left: pos.x, top: pos.y }}
            onMouseEnter={clearHideTimer}
            onMouseLeave={scheduleHide}
            role="tooltip"
            aria-hidden="true"
          >
            {tip}
          </div>,
          portalRoot,
        )
      : null

  return (
    <>
      <div
        className={className}
        onMouseEnter={openOnceAtPointer}
        onMouseLeave={scheduleHide}
        onFocusCapture={onFocusCapture}
        onBlurCapture={scheduleHide}
      >
        {children}
      </div>
      {portal}
    </>
  )
}
